import _ from 'lodash';
import $ from 'cheerio';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import parserDomNode from 'html-react-parser';
import { setMetaTags } from 'lib/frankly';
import { DataStoreDispatcher } from '../../lib/frankly';
import { bylineToString, authorsToString } from 'utils/NewsUtils';
import { generateStyles } from '../../utils/DOMUtils';
import { buildURLFingerprinting } from '../../../shared/utils/UrlUtils';
import ComponentLoader from '../../../shared/utils/ComponentLoader';
import Image from 'components/Image';
import Timestamp from 'components/Timestamp';
import Video from 'components/media/Video';
import AdvertisementUnit from 'components/AdvertisementUnit';
import { getCurrentWindowBreakpointSize } from '../../utils/WindowUtils';
import ComponentContainer from '../core/ComponentContainer';

const ADDTHIS_POSITION = {
  BELOW_BODY: 'below-body',
  ABOVE_BODY: 'above-body'
};

const REMOVE_HTML_TAGS_REGEX = new RegExp(/(<style.*>.*<\/style>)|(<script.*>.*<\/script>)|(<(?!((\/?img)|(\/?iframe)|(\/?video)|(\/?table)))[^>]*>)/, 'g');
const PARAGRAPH_TAGS_REGEX = new RegExp(/(<style.*>.*<\/style>)|(<script.*>.*<\/script>)|(<noscript.*>.*<\/noscript>)|(<table.*>.*<\/table>)|(<[^>]*>)/, 'g');

class Article extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    FRN_requestError: PropTypes.object,
    pillColor: PropTypes.string,
    showAbstract: PropTypes.bool,
    isHeroAboveTitleBlock: PropTypes.bool,
    maintainHeroImageAspectRatio: PropTypes.bool,
    heroImageBackgroundColor: PropTypes.string,
    showStoryAds: PropTypes.bool,
    adPlacementPattern: PropTypes.number,
    minRequiredCharacterNumber: PropTypes.number,
    ads: PropTypes.array,
    showTimestamp: PropTypes.bool,
    showDateline: PropTypes.bool,
    autoPlayVideo: PropTypes.bool,
    showDockedVideo: PropTypes.bool,
    dockVideoBreakpoints: PropTypes.array,
    dockVideoPosition: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
    addMetaTags: PropTypes.bool,
    replacedAds: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.number.isRequired,
        ad: PropTypes.shape({
          id: PropTypes.string.isRequired
        }),
        screenSizes: PropTypes.array.isRequired
      })
    ),
    comments: PropTypes.object,
    slideshow: PropTypes.shape({
      componentId: PropTypes.string.isRequired,
      componentProps: PropTypes.object
    }),
    addthisOptions: PropTypes.shape({
      className: PropTypes.string.isRequired,
      position: PropTypes.oneOf([ADDTHIS_POSITION.BELOW_BODY, ADDTHIS_POSITION.ABOVE_BODY])
    }),
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    separateByline: PropTypes.bool,
    showEmailAndSocialConnect: PropTypes.bool
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    addMetaTags: false,
    minRequiredCharacterNumber: 40,
    showDateline: false,
    separateByline: false,
    dockVideoBreakpoints: ['xl', 'lg', 'md', 'sm']
  }

  state = {
    scripts: [],
    bodyHtml: '',
    CommentsComponentClass: null
  }

  componentWillMount() {
    this._setup();
  }

  componentDidMount() {
    this._post_setup();
    window.addEventListener('resize', () => this._updateCurrentWindowsBreakpointSize());
  }

  componentWillReceiveProps(nextProps) {
    if (Array.isArray(this.props.FRN_rawResponses) && this.props.FRN_rawResponses.length === 0
        && Array.isArray(nextProps.FRN_rawResponses) && nextProps.FRN_rawResponses.length > 0) {
      this._setup();
    }
  }

  componentDidUpdate() {
    if (Array.isArray(this.props.FRN_rawResponses) && this.props.FRN_rawResponses.length > 0) {
      this._post_setup();
    }
  }

  componentWillUnmount() {
    const {
      flux
    } = this.context;

    flux.getActions('Helmet').update({ title: '' });

    const wngPageInfo = (typeof window !== 'undefined' && window["wng_pageInfo"]) || {};

    delete wngPageInfo.containerId;
    delete wngPageInfo.headline;
    delete wngPageInfo.pageTitle;
    delete wngPageInfo.baseUrl;
    delete wngPageInfo.permaLink;
    delete wngPageInfo.contentClassification;
    delete wngPageInfo.ownerAffiliateName;
    delete wngPageInfo.ownerAffiliateId;
    delete wngPageInfo.containerClass;
    delete wngPageInfo.content_authors;

    window.removeEventListener('resize', () => this._updateCurrentWindowsBreakpointSize());
  }

  _setup = () => {
    const {
      comments: {
        componentId: commentsComponentId = ''
      } = {},
      FRN_rawResponses: [
        {
          data: {
            headline = '',
            body = '',
            link = '',
            ads: {
              showlocal = true,
              shownational = true
            } = {},
            storyimages: [{
              filename: heroImageFilename = '',
              lastEditedDate = ''
              }] = [{}],
            seo: {
              description = '',
              keywords = '',
              robot = '',
              canonicallink,
            } = {},
            options: {
              socialtools: {
                comments: {
                  display: displayComments = false
                } = {}
              } = {}
            } = {}
          } = {}
        } = {}
      ] = [],
      addMetaTags = false
    } = this.props;

    if (process.env.BROWSER) {
      const {
        flux
      } = this.context;
      flux.getActions('Helmet').update({ title: headline });
      flux.getActions('Advertisement').updateAds(showlocal || shownational);

      if (displayComments && commentsComponentId) {
        ComponentLoader.importReactComponent(commentsComponentId).then(CommentsComponentClass =>
          this.setState({ CommentsComponentClass })
        ).catch(error => {
            throw new Error(error);
          }
        );
      }

      if (body) {
        const scripts = body.match(/<script[\s\S]*?>[\s\S]*?<\/script>/gi);
        const bodyHtmlWithoutScripts = body.split(/<script[\s\S]*?>[\s\S]*?<\/script>/gi).join('');

        this.setState({
          bodyHtml: bodyHtmlWithoutScripts,
          scripts
        });
      }

      this._updateCurrentWindowsBreakpointSize();
    }

    if (addMetaTags) {
      const imageURL = buildURLFingerprinting({url: heroImageFilename, lastEditedDate});

      setMetaTags(this, [{
        name: 'description',
        content: description
      }, {
        name: 'keywords',
        content: keywords
      }, {
        name: 'robots',
        content: robot
      }, {
        rel: 'canonical',
        href: canonicallink || link,
        isLink: true
      }, {
        property: 'og:title',
        content: headline
      }, {
        property: 'og:type',
        content: 'article'
      }, {
        property: 'og:url',
        content: link
      }, {
        property: 'og:image',
        content: imageURL
      }, {
        property: 'og:description',
        content: description
      }, {
        name: 'twitter:card',
        content: 'summary_large_image'
      }, {
        name: 'twitter:description',
        content: description
      }, {
        name: 'twitter:title',
        content: headline
      }, {
        name: 'twitter:image',
        content: imageURL
      }]);
    }
  }

  _post_setup = () => {
    const {
      FRN_rawResponses: [
        {
          data: {
            id: containerId = '',
            headline = '',
            contentClassification = '',
            type: containerClass = '',
            link = '',
            bylines = [],
            owner: {
              name: ownerName = '',
              id: ownerId = null
            } = {}
          } = {}
        } = {}
      ] = []
    } = this.props;
    const wngPageInfo = (typeof window !== 'undefined' && window["wng_pageInfo"]) || {};
    const baseUrl = link.substring(0, link.indexOf("/", 7));
    const permaLink = link.substring(link.indexOf("/", 7));
    const authors = authorsToString(_.map(bylines, bylineToString));

    wngPageInfo.containerId = containerId;
    wngPageInfo.headline = headline;
    wngPageInfo.pageTitle = headline;
    wngPageInfo.baseUrl = baseUrl;
    wngPageInfo.permaLink = permaLink;
    wngPageInfo.contentClassification = contentClassification;
    wngPageInfo.ownerAffiliateName = ownerName;
    wngPageInfo.ownerAffiliateId = Number(ownerId);
    wngPageInfo.containerClass = containerClass;
    wngPageInfo.content_authors = authors;

    //Set the new wng_pageInfo variable for use by external parties
    window["wng_pageInfo"] = wngPageInfo;

    this._articleDataStoreDispatcher = new DataStoreDispatcher('franklyinc.com/article');
    this._articleDataStoreDispatcher.set('authors', authors);
    this._articleDataStoreDispatcher.set('articleId', containerId);
  }

  _getScripts = () => {
    const {
        FRN_rawResponses: [
          {
            data: {
              body = '',
            } = {}
          } = {}
        ] = [],
      } = this.props;

    let {scripts = []} = this.state;

    if (body) {
      if (!scripts || scripts.length === 0) {
        scripts = body.match(/<script[\s\S]*?>[\s\S]*?<\/script>/gi);
        //scripts = body.match(/<script[\s\S]*?>[\s\S]*?<\/script>/gi).replace(/</g, '\\u003c');
      }
    }
    return scripts;
  }

  _getBodyHtml = () => {
    const {
      FRN_rawResponses: [
        {
          data: {
            body = '',
          } = {}
        } = {}
      ] = [],
    } = this.props;

    let {bodyHtml} = this.state;

    if (body) {
      if (!bodyHtml) {
        const bodyHtmlWithoutScripts = body.split(/<script[\s\S]*?>[\s\S]*?<\/script>/gi).join('');
        bodyHtml = bodyHtmlWithoutScripts;
      }
    }

    return bodyHtml;
  }

  _updateCurrentWindowsBreakpointSize() {
    this.setState({ currentWindowsBreakpointSize: getCurrentWindowBreakpointSize() });
  }

  render() {    
    if (!this.props.FRN_rawResponses) {
      return null;
    }

    const {
      pillColor = '#000000',
      ads: configAds = [],
      replacedAds = [],
      showStoryAds,
      adPlacementPattern,
      minRequiredCharacterNumber,
      autoPlayVideo,
      showDockedVideo,
      dockVideoBreakpoints,
      dockVideoPosition,
      showTimestamp = true,
      showAbstract = true,
      showDateline,
      isHeroAboveTitleBlock = true,
      maintainHeroImageAspectRatio = false,
      heroImageBackgroundColor = '#FFFFFF',
      comments: {
        componentProps: commentsComponentProps = {}
      } = {},
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {},
      addthisOptions: {
        className: addThisClassName,
        position: addthisPosition = ADDTHIS_POSITION.ABOVE_BODY
      } = {},
      slideshow = {},
      FRN_rawResponses = [],
      separateByline,
      showEmailAndSocialConnect = false
    } = this.props;

    const [
      {
        data: {
          headline = '',
          dateline = '',
          abstract = '',
          bylines = [],
          features = [],
          storyimages: storyImages = [],
          contentClassification: category = '',
          publishedDate: publishDate = '',
          updatedDate: updatedDate = '',
          ads: {
            ads: storyAds = []
          } = {},
          options: {
            date: {
              published: {
                showonstory: showPublishedOnStory = true
              } = {},
              updated: {
                showonstory: showUpdatedOnStory = true
              } = {}
            } = {}
          } = {}
        } = {}
      } = {}
    ] = FRN_rawResponses;

    const {
      componentId: slideshowComponentId
    } = slideshow;

    let {
      bodyHtml,
      scripts,
      CommentsComponentClass,
      currentWindowsBreakpointSize
    } = this.state;

    if (!scripts || scripts.length === 0) {
      scripts = this._getScripts();
    }

    if (!bodyHtml) {
      bodyHtml = this._getBodyHtml();
    }

    const addThisContent = <div className={addThisClassName} />;
    const videoClip = _.find(features, feature => feature.type === 'clip');

    if (!bodyHtml && !videoClip) {
      return null;
    }

    const { config: { theme: { primaryColor = '' } = {} } = {} } = this.context;
    const componentClassName = 'Article';
    const pill = category ? (     //TODO: Make common component to be shared across article list
      <div className="Article-pill" style={{ backgroundColor: pillColor }}>
        {category}
      </div>) : null;

    const isDuplicateTimestamp = showPublishedOnStory && showUpdatedOnStory;
    const hasUpdatedTimestamp =  showUpdatedOnStory && (Date.parse(publishDate) !== Date.parse(updatedDate));
    let hasSeparateByline = separateByline && !isDuplicateTimestamp;
    if (!hasUpdatedTimestamp && separateByline) {
      hasSeparateByline = true;
    }
    const authors = _.map(bylines, bylineToString);
    const timeStamp = <Timestamp bylines={bylines} showEmailAndSocialConnect={showEmailAndSocialConnect} separateByline={hasSeparateByline} publishDate={publishDate} authors={(separateByline && showUpdatedOnStory) ? null : authors} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>;
    const storyStyles = generateStyles('.' + componentClassName, [
      {
        selector: 'blockquote',
        styles: 'color: ' + primaryColor + ';'
      },
      {
        selector: 'a',
        styles: 'color: ' + primaryColor + ';'
      }
    ]);

    const isDockShowed = showDockedVideo && dockVideoBreakpoints.indexOf(currentWindowsBreakpointSize) > -1;

    const articleVideo = videoClip ? <Video clipId={videoClip.id} autoPlay={autoPlayVideo} showDockedVideo={isDockShowed} dockPosition={dockVideoPosition}/> : null;
    let featuredImages = null;

    if (storyImages.length) {
      featuredImages = (slideshowComponentId && storyImages.length > 1) ? <Slideshow FRN_rawResponses={FRN_rawResponses} componentDefinition={slideshow} hasStoryImages = {true}/> : <Hero heroImage={storyImages[0]} maintainHeroImageAspectRatio={maintainHeroImageAspectRatio} heroImageBackgroundColor={heroImageBackgroundColor} />;
    }

    const heroBlock = articleVideo || featuredImages;
    let ads = [];

    if (configAds.length) {
      ads = configAds.map(item => {
        const {
          adData: {
            wncc
          } = {}
        } = item;

        _.set(item, 'adData.wncc', wncc || category);

        return item;
      });
    } else {
      storyAds.forEach(item => {
        const {
          type,
          sz,
          owner,
          splitpct,
          show
        } = item;

        if (show && type === 'ad') {
          // substring to get id from sz (ex: wnsz_23)
          const adId = sz.substring(5);

          ads.push({
            isAutoAd: true,
            adData: {
              id: adId,
              wncc: category,
              ownerinfo: {
                oldformat: 1,
                owner,
                split: splitpct
              }
            }
          });
        }
      });
    }

    return (
      <div className="Article">
          <div className="Article-contents">
            { isHeroAboveTitleBlock ? heroBlock : null }
            <div className="Article-boundingContainer">
              { pill }
              { headline ? <h1 className="Article-title">{ headline }</h1> : null }
              { abstract && showAbstract ?
                <div className="Article-abstract" dangerouslySetInnerHTML={{ __html: abstract }} ></div>
                : null}
              { (showTimestamp && showPublishedOnStory) ? timeStamp : null }
              { (showTimestamp && hasUpdatedTimestamp) ?
                (<div className="Timestamp-updated" >
                  <span className="Timestamp Timestamp-prefix" >Updated: </span>
                  <Timestamp showEmailAndSocialConnect={showEmailAndSocialConnect} bylines={bylines} separateByline={separateByline} authors={separateByline ? authors : null} publishDate={updatedDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
                </div>)
                : null}
            </div>
            { isHeroAboveTitleBlock ? null : heroBlock }
            <div className="Article-boundingContainer">
              {addthisPosition === ADDTHIS_POSITION.ABOVE_BODY && addThisContent}
              <ArticleBody
                showStoryAds={showStoryAds}
                adPlacementPattern={adPlacementPattern}
                minRequiredCharacterNumber={minRequiredCharacterNumber}
                ads={ads}
                bodyHtml={bodyHtml}
                scripts={scripts}
                replacedAds={replacedAds}
                showDateline={showDateline}
                dateline={dateline}
                />
                {addthisPosition === ADDTHIS_POSITION.BELOW_BODY && addThisContent}
            </div>
            <style>
              { storyStyles }
            </style>
          </div>
        { CommentsComponentClass ? <CommentsComponentClass {...commentsComponentProps} /> : null }
      </div>
    );
  }
}
/**
 * Class representing the Slideshow at the top of the Story Page
 */
class Slideshow extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    componentDefinition: PropTypes.shape({
      componentId: PropTypes.string.isRequired,
      componentProps: PropTypes.object
    }),
    hasStoryImages: PropTypes.bool
  }

  state = {
    ComponentClass: null
  }

  componentWillMount() {
      const {
        componentDefinition: {
          componentId: slideshowComponentId = ''
        } = {}
      } = this.props;

      if (slideshowComponentId) {
        ComponentLoader.importReactComponent(slideshowComponentId).then(ComponentClass =>
          this.setState({ ComponentClass })
        ).catch(error => {
            throw new Error(error);
          }
        );
      }
  }

  render() {
    const { ComponentClass } = this.state;
    const {
      FRN_rawResponses: [{
        data
      }],
      componentDefinition: {
        componentProps = {}
      },
      hasStoryImages
    } = this.props;

    componentProps['showTitle'] = false;
    componentProps['FRN_rawResponses'] = [{'data': _.omit(data, ['features'])}];
    componentProps['hasStoryImages'] = hasStoryImages;

    return (
      <div className="Slideshow">
        { ComponentClass ? <ComponentClass {...componentProps}/> : null }
      </div>
    );
  }
}
/**
 * Class representing the Hero Image at the top of the Story Page
 */
class Hero extends Component {
  static propTypes = {
    heroImage: PropTypes.object,
    maintainHeroImageAspectRatio: PropTypes.bool,
    heroImageBackgroundColor: PropTypes.string,
  }

  render() {
    const {
      heroImage: {
        filename = '',
        caption = '',
        lastEditedDate = '',
      } = {},
      maintainHeroImageAspectRatio = false,
      // heroImageBackgroundColor = '#FFFFFF'
    } = this.props;

    const heroImageStyle = {};

    if (maintainHeroImageAspectRatio) {
      heroImageStyle['objectFit'] = 'contain';
    } else {
      heroImageStyle['objectFit'] = 'cover';
    }

    const heroImageClassNames = classNames(
      'Hero-image',
      {'Hero-image-ratio': maintainHeroImageAspectRatio}
    );

    const imageClassNames = `Hero-image--${maintainHeroImageAspectRatio ? 'scale' : 'accessible'}`;

    return (
      <div className="Hero">
        <div className={heroImageClassNames}>
          <Image height={'560'} src={filename} lastEditedDate={lastEditedDate} alt={caption} imageClassNames={imageClassNames} imageStyles={heroImageStyle} />
        </div>
        {caption ? <div className="Hero-caption">{caption}</div> : null}
      </div>
    );
  }
}

/**
 * Class representing the Body of the Article. This data comes from our CMS
 */
class ArticleBody extends Component {
  static propTypes = {
    showStoryAds: PropTypes.bool,
    adPlacementPattern: PropTypes.number,
    minRequiredCharacterNumber: PropTypes.number,
    ads: PropTypes.array,
    bodyHtml: PropTypes.string,
    scripts: PropTypes.array,
    replacedAds: PropTypes.array,
    showDateline: PropTypes.bool,
    dateline: PropTypes.string
  }

  //WEB-4658: bacause using NGW slideShow, so remove <script> for slideShow 
  _getScriptsForBody() {
    let { scripts = [], bodyHtml = '' } = this.props;

    //get all widget ID for slideshow
    let widgetIds = bodyHtml.match(/[0-9]+-slideshow/g) || [];
    widgetIds.forEach((item, i) => {
      widgetIds[i] = item.replace('-slideshow', '');
    });

    // remove <script> if this using for slideShow
    scripts.forEach((script, i) => {
      if (widgetIds.some((widgetId) => script.indexOf(widgetId) > -1)) {
        scripts[i] = '';
      }
    });

    return scripts;
  }

  _handleBodyDidMount(ref) {
    if (process.env.BROWSER) {
      const jQuery = window['$'];
      let scripts = this._getScriptsForBody();
      if (Array.isArray(scripts) && scripts.length > 0) {
        jQuery(ref).append(scripts);
      }

      let isExecuted = false;

      if (window.FB && window.FB.init) {
        isExecuted = true;
        window.FB.init({
          autoLogAppEvents: true,
          status: true,
          xfbml: true,
          version: 'v2.9'
        });
      }

      let count = 0;
      let id = setInterval(() => {
        if (!isExecuted && window.FB && window.FB.init) {
          isExecuted = true;
          window.FB.init({
            autoLogAppEvents: true,
            status: true,
            xfbml: true,
            version: 'v2.9'
          });
        }
        if (isExecuted || count++ >= 10) clearInterval(id);
      }, 3000);
    }
  }

  _convertHTMLElementToString(element) {
    return $('<div>').append(element).html();
  }

  _buildDatelineTemplate(dateline) {
    return `
      <b>${dateline} - </b>
    `;
  }

  _getBodyComponents(bodyHtml = '', ads = [], replacedAds = [], pattern = 3, showStoryAds, minRequiredCharacterNumber = 0, showDateline, dateline) {
    const components = [];

    let bodyElements;

    if (!bodyHtml) {
      return null;
    }

    const enableDateline = showDateline && dateline;

    // WEB-4658: replace legacy slideShow by NGW slideShow
    // if (!showStoryAds && !enableDateline) {
    //   return <div dangerouslySetInnerHTML={{ __html: bodyHtml }}></div>;
    // }

    /* This is pulling all of the top level children from the block of body HTML that is handed down */
    bodyElements = Array.from($(bodyHtml)).map((element) => this._convertHTMLElementToString(element));

    if (enableDateline) {
      /** Add dateline to the first full paragraph which contains only text */
      const datelineIndex = bodyElements.findIndex(paragraph => paragraph.replace(PARAGRAPH_TAGS_REGEX, '').replace(/&nbsp;|&#xA0;/g, ' ').trim().length > 0);

      if (datelineIndex !== -1) {
        const datelineContent = $(bodyElements[datelineIndex]).prepend(this._buildDatelineTemplate(dateline));
        bodyElements[datelineIndex] = this._convertHTMLElementToString(datelineContent);
      }
    }

    if (!showStoryAds) {
      _.forEach(bodyElements, (paragraph, index) => {
        // WEB-4658: replace legacy slideShow by NGW slideShow
        components.push(<Paragraph paragraph={paragraph} key={index}/>);
      });

      return components;
    }

    let numberOfValidParagraphs = 0;

    _.forEach(bodyElements, (paragraph, index) => {
      // WEB-4658: replace legacy slideShow by NGW slideShow
      components.push(<Paragraph paragraph={paragraph} key={index}/>);

      /** A valid paragraph:
       * Is not empty: have at least one word / image / iframe / video / table, space character will not be counted (&nbsp; or &#xA0;)
       * Not a style / script html tag
       */
      const isValidParagraph = paragraph.replace(REMOVE_HTML_TAGS_REGEX, '').replace(/&nbsp;|&#xA0;/g, ' ').trim().length > minRequiredCharacterNumber;

      const currentAdIndex = Math.floor(numberOfValidParagraphs / pattern);

      if (isValidParagraph) {
        if (ads[currentAdIndex] && numberOfValidParagraphs % pattern === pattern - 1) {
          const insertedAds = this._insertAds(ads, replacedAds, currentAdIndex);

          components.push(...insertedAds);
        }

        numberOfValidParagraphs++;
      }
    });

    // If there are not enough paragraphs to put an advertisement between paragraphs, put one at the end
    if (numberOfValidParagraphs < pattern && ads.length) {
      const insertedAds = this._insertAds(ads, replacedAds, 0);

      components.push(...insertedAds);
    }

    return components;
  }

  _insertAds(ads, replacedAds, index) {
    const components = [];
    const curAdUnit = ads[index];
    const replaceAd = replacedAds.find(item => item.position === index);
    let adUnitClassnames = curAdUnit.className;

    if (replaceAd) {
      /** We need to distinguish Ad container from paragraph container to prevent CSS override */
      let replacedAdClassnames = 'ArticleBody-ad';

      const {
        ad: {
          id,
          props
        },
        screenSizes = []
      } = replaceAd;

      screenSizes.forEach(screenSize => {
        adUnitClassnames += ` hidden-${screenSize}`;
        replacedAdClassnames += ` visible-${screenSize} `;
      });

      components.push(<ReplacedAd key={index + 'replacedAd'} componentInstanceId={id} componentDefinition={props} classnames={replacedAdClassnames} />);
    }

    components.push(<AdvertisementUnit key={index + '-ad'} {...curAdUnit} className={adUnitClassnames} />);

    return components;
  }

  render() {
    const {
      ads = [],
      replacedAds = [],
      showStoryAds,
      adPlacementPattern,
      minRequiredCharacterNumber,
      bodyHtml,
      showDateline,
      dateline
    } = this.props;

    const articleBody = this._getBodyComponents(bodyHtml, ads, replacedAds, adPlacementPattern, showStoryAds, minRequiredCharacterNumber, showDateline, dateline);

    return (
      <div className="ArticleBody" ref={ref => this._handleBodyDidMount(ref)}>
        {articleBody}
      </div>
    );
  }
}

class ReplacedAd extends Component {
  static propTypes = {
    componentInstanceId: PropTypes.string.isRequired,
    componentDefinition: PropTypes.object.isRequired,
    classnames: PropTypes.string
  }

  state = {
    ComponentClass: null
  }

  componentWillMount() {
    const {
      componentInstanceId
    } = this.props;

    if (componentInstanceId) {
      ComponentLoader.importReactComponent(componentInstanceId).then(ComponentClass =>
        this.setState({ ComponentClass })
      ).catch(error => {
        throw new Error(error);
      });
    }
  }

  render() {
    const {
      componentDefinition,
      classnames
    } = this.props;

    const {
      ComponentClass
    } = this.state;

    if (!ComponentClass) {
      return null;
    }

    return (
      <div className={classnames}>
        <ComponentClass {...componentDefinition} />
      </div>
    );
  }
}

class Paragraph extends Component {
  static propTypes = {
    paragraph: PropTypes.string
  }

  getSlideShowDom(paragraph, slideShowWidgetIds) {
    return parserDomNode(paragraph, {
      replace: (domNode) => {
        //find domNode has slideshow and replaced by NGW slideshow
        if (domNode.attribs && slideShowWidgetIds.indexOf(domNode.attribs.id) > -1) {
          const widgetId = domNode.attribs.id.split('-')[0];
          const widgetComponentDefinition = {
            id: 'components/news/Slideshow',
            FRN_resourceEndpoints: [{
              url: `/feed/v3.0/widgets/${widgetId}?alt=json`,
              type: 'json'
            }]
          };

          return (
            <div>
              <ComponentContainer
                componentDefinition={widgetComponentDefinition}
                params={{}}
                widgetId={widgetId}
                componentInstanceId={`slideshow-${widgetId}`} />
            </div>);
        }
      }
    });;
  }

  render() {
    const {
      paragraph
    } = this.props;

    //get slideshow ids
    const slideShowWidgetIds = paragraph.match(/[0-9]+-slideshow/g) || [];

    return (
      <div>
        { slideShowWidgetIds.length > 0 && this.getSlideShowDom(paragraph, slideShowWidgetIds) }
        { slideShowWidgetIds.length === 0 && <div className="Article-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }}></div> }
      </div>
    );
  }
}

export default Article;



// WEBPACK FOOTER //
// ./app/components/news/Article.js